/**
 * Detects if the current device is a mobile device (phone or tablet).
 *
 * Uses user-agent detection because the goal is to determine device capability
 * (e.g., whether the user can install a Chrome extension), not viewport size.
 * A desktop browser in a narrow window can still install extensions.
 */
export const isMobileDevice = (): boolean => {
  if (typeof navigator === 'undefined') return false;

  const ua = navigator.userAgent;

  // Standard mobile user-agent patterns
  if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua)) {
    return true;
  }

  // iPadOS 13+ reports a Mac-like user agent but exposes multi-touch
  if (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1) {
    return true;
  }

  return false;
};

export type DesktopOs = 'mac' | 'windows' | 'unknown';
export type DesktopArch = 'arm' | 'intel' | 'unknown';

export interface DesktopPlatform {
  os: DesktopOs;
  arch: DesktopArch;
}

const UNKNOWN_PLATFORM: DesktopPlatform = { os: 'unknown', arch: 'unknown' };

/**
 * Minimal shape of the User-Agent Client Hints API (Chromium only, not in lib.dom).
 */
interface UserAgentDataLike {
  getHighEntropyValues?: (hints: string[]) => Promise<{ architecture?: string; platform?: string }>;
}

/**
 * Resolve the OS from a platform/user-agent string. Returns 'unknown' for
 * anything that isn't macOS or Windows (Linux, ChromeOS, unrecognized).
 */
const parseOs = (value: string): DesktopOs => {
  if (/mac/i.test(value)) return 'mac';
  if (/win/i.test(value)) return 'windows';
  return 'unknown';
};

/**
 * Detect the visitor's desktop OS and CPU architecture, to pick the matching
 * desktop app build.
 *
 * Architecture comes ONLY from `navigator.userAgentData.getHighEntropyValues`
 * — the user-agent string always reports "Intel Mac OS X" even on Apple
 * Silicon, so it can never be used to infer Mac architecture. When the
 * architecture can't be read the result is `arch: 'unknown'`, and callers show
 * every build for the detected OS.
 *
 * Never throws: any failure degrades to 'unknown' rather than blocking render.
 */
export const detectDesktopPlatform = async (): Promise<DesktopPlatform> => {
  if (typeof navigator === 'undefined') return UNKNOWN_PLATFORM;

  // User-agent string gives us the OS only — never the Mac architecture.
  const uaOs = parseOs(navigator.userAgent || '');

  try {
    const uaData = (navigator as Navigator & { userAgentData?: UserAgentDataLike }).userAgentData;

    if (uaData?.getHighEntropyValues) {
      const values = await uaData.getHighEntropyValues(['architecture', 'platform']);
      const hintedOs = values.platform ? parseOs(values.platform) : 'unknown';
      const arch = values.architecture === 'arm' ? 'arm' : values.architecture === 'x86' ? 'intel' : 'unknown';

      return { os: hintedOs === 'unknown' ? uaOs : hintedOs, arch };
    }
  } catch {
    // Client Hints unavailable or rejected — fall back to the OS-only result.
  }

  return { os: uaOs, arch: 'unknown' };
};
